import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import PasswordInput from './ui/PasswordInput';
import { toast } from 'sonner';
import { CheckIcon } from 'lucide-react';
import Header from './Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserRegistration = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [formData, setFormData] = useState({
    full_name: '',
    password: '',
    confirm_password: '',
    token: token || ''
  });
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);

  useEffect(() => {
    if (!token) {
      toast.error('Geçersiz davet linki');
      navigate('/login');
      return;
    }
    
    // Validate token and get invite details
    const validateToken = async () => {
      try {
        const response = await axios.get(`${API}/invites/validate/${token}`);
        setTokenValid(true);
        
        // Do NOT pre-fill full name - let user enter it themselves
        // Email and company info will be validated from token on submission
      } catch (error) {
        console.error('Token validation error:', error);
        toast.error('Geçersiz veya süresi dolmuş davet linki');
        setTokenValid(false);
        setTimeout(() => navigate('/login'), 2000);
      }
    };
    
    validateToken();
  }, [token, navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirm_password) {
      toast.error('Şifreler eşleşmiyor');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Şifre en az 6 karakter olmalıdır');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/users/register`, {
        full_name: formData.full_name,
        password: formData.password,
        token: formData.token
      });

      // Store the token
      localStorage.setItem('token', response.data.access_token);
      
      toast.success('Hesabınız başarıyla oluşturuldu! Dashboard\'a yönlendiriliyorsunuz...');
      
      // Redirect to dashboard
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);

    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Kayıt sırasında bir hata oluştu';
      toast.error(errorMessage);
      
      if (errorMessage.includes('Invalid') || errorMessage.includes('expired')) {
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }
    } finally {
      setLoading(false);
    }
  };

  if (tokenValid === null) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-sky-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          {/* Page Title */}
          <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center mr-3">
              <CheckIcon className="w-5 h-5 text-teal-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">
              Hesap Aktivasyonu
            </h2>
          </div>
          <p className="text-gray-600">
            Takım davetiniz onaylandı! Hesabınızı tamamlamak için aşağıdaki bilgileri doldurun.
          </p>
        </div>

        {/* Registration Form */}
        <Card className="p-8 shadow-xl border-0">
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="user-registration-form">
            <div>
              <Label htmlFor="full_name" className="form-label">
                Ad Soyad *
              </Label>
              <Input
                id="full_name"
                name="full_name"
                type="text"
                value={formData.full_name}
                onChange={handleChange}
                className="form-input"
                placeholder="Ahmet Yılmaz"
                required
                data-testid="fullname-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Bu bilgi dashboard'da görüntülenecektir
              </p>
            </div>

            <div>
              <Label htmlFor="password" className="form-label">
                Şifre *
              </Label>
              <PasswordInput
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="form-input"
                placeholder="••••••••"
                required
                minLength={6}
                data-testid="password-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                En az 6 karakter olmalıdır
              </p>
            </div>

            <div>
              <Label htmlFor="confirm_password" className="form-label">
                Şifre Tekrar *
              </Label>
              <PasswordInput
                id="confirm_password"
                name="confirm_password"
                value={formData.confirm_password}
                onChange={handleChange}
                className="form-input"
                placeholder="••••••••"
                required
                minLength={6}
                data-testid="confirm-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading || !formData.full_name || !formData.password || !formData.confirm_password}
              className="btn-primary w-full flex justify-center py-3"
              data-testid="activate-account-btn"
            >
              {loading ? (
                <div className="loading-spinner"></div>
              ) : (
                <span>Hesabımı Aktive Et</span>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Hesap aktivasyonunda sorun mu yaşıyorsunuz?{' '}
              <a href="mailto:destek@quattroflow.com" className="font-medium text-teal-600 hover:text-teal-500">
                Destek ekibiyle iletişime geçin
              </a>
            </p>
          </div>
        </Card>

        {/* Security Note */}
        <Card className="mt-4 p-4 bg-blue-50 border border-blue-200">
          <div className="flex items-start space-x-3">
            <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center mt-0.5">
              <CheckIcon className="w-3 h-3 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-blue-800 mb-1">Güvenlik Bildirimi</h3>
              <p className="text-sm text-blue-700">
                Bu davet linki sadece 7 gün geçerlidir ve tek kullanımlıktır. 
                Hesabınızı aktive ettikten sonra güvenli bir şekilde sisteme giriş yapabilirsiniz.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
    </>
  );
};

export default UserRegistration;